// Групите на „Целия списък": на десктоп — лява релса („Всички" първа, после
// групите азбучно, „Без група" накрая) с броя на съвпаденията СЛЕД текста и
// превключвателите; на телефон — същите като хоризонтално скролиращи чипове.
// Групите не изчезват при 0 съвпадения (само избледняват) — релсата не скача,
// докато се пише.
//
// Клавиатура: от филтъра Alt+↑/↓ сменя групата без да мести фокуса (това е
// бързият път). Tab влиза в релсата на избраната група (roving tabindex);
// там ↑/↓ (←/→ на чиповете) и Home/End сменят групата веднага, Enter/Space/клик
// я избират и (на десктоп) връщат фокуса във филтъра; Esc — също (тялото).
// На телефон изборът не вдига клавиатурата — фокусът не се връща.
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ALL_GROUPS, NO_GROUP } from "./catalogPickerModel";

type Props = {
  groups: string[];
  group: string;
  counts: Map<string, number>;
  total: number;
  isNarrow: boolean;
  onChoose: (value: string, refocus?: boolean) => void;
};

const FOCUS_RING = "outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]";

export const CatalogGroupRail = ({ groups, group, counts, total, isNarrow, onChoose }: Props) => {
  const { t } = useTranslation();
  const railRef = useRef<HTMLDivElement | null>(null);
  const ring = [ALL_GROUPS, ...groups];

  // Избраната група винаги е в полезрението (и при Alt+↑/↓ от филтъра)
  useEffect(() => {
    railRef.current
      ?.querySelector<HTMLElement>('[aria-pressed="true"]')
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [group]);

  const labelOf = (value: string) =>
    value === ALL_GROUPS
      ? t("inventoryPage.catalogAllGroups")
      : value === NO_GROUP
        ? t("inventoryPage.stocktakeNoGroup")
        : value;
  const countOf = (value: string) => (value === ALL_GROUPS ? total : (counts.get(value) ?? 0));

  // Стрелките в релсата: сменят групата и фокуса, без да се връщат във филтъра
  const onRailKeyDown = (event: React.KeyboardEvent) => {
    const position = Math.max(0, ring.indexOf(group));
    const back = event.key === "ArrowUp" || event.key === "ArrowLeft";
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    let next: number | null = null;
    if (back) next = Math.max(0, position - 1);
    else if (forward) next = Math.min(ring.length - 1, position + 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = ring.length - 1;
    if (next == null) return;
    event.preventDefault();
    event.stopPropagation();
    onChoose(ring[next], false);
    railRef.current?.querySelector<HTMLElement>(`[data-group-index="${next}"]`)?.focus();
  };

  return (
    <div
      ref={railRef}
      role="group"
      aria-label={t("inventoryPage.catGroups")}
      onKeyDown={onRailKeyDown}
      className={
        isNarrow
          ? "flex gap-1.5 overflow-x-auto px-4 pb-2.5 [scrollbar-width:none]"
          : "flex w-[216px] shrink-0 flex-col gap-px overflow-y-auto border-r border-shade-2 bg-panel px-2 py-2.5"
      }
    >
      {!isNarrow && (
        <span className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase leading-[normal] tracking-[0.07em] text-muted">
          {t("inventoryPage.catGroups")}
        </span>
      )}
      {ring.map((value, index) => {
        const on = value === group;
        const count = countOf(value);
        const tone = on
          ? "bg-brand-tint font-semibold text-brand-deep-ink"
          : count
            ? "bg-transparent text-ink-2 hover:bg-fill"
            : "bg-transparent text-faint hover:bg-fill";
        return (
          <button
            key={value}
            type="button"
            aria-pressed={on}
            tabIndex={on ? 0 : -1}
            data-group-index={index}
            // Клик с мишката не взема фокуса от филтъра
            onMouseDown={isNarrow ? undefined : (event) => event.preventDefault()}
            onClick={() => onChoose(value, !isNarrow)}
            className={
              isNarrow
                ? `flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-[6px] text-[13px] leading-[normal] ${FOCUS_RING} ${
                    on ? "border-brand/45 bg-brand-tint font-semibold text-brand-deep-ink" : `border-shade-3 bg-surface ${count ? "text-ink" : "text-faint"}`
                  }`
                : `flex w-full cursor-pointer items-center gap-2 rounded-lg border-none px-2.5 py-[7px] text-left text-[13px] leading-[1.25] ${FOCUS_RING} ${tone}`
            }
          >
            <span className="min-w-0 flex-1 truncate">{labelOf(value)}</span>
            <span className={`font-sheet text-[11.5px] tabular-nums ${on ? "" : "text-muted"}`}>{count}</span>
          </button>
        );
      })}
    </div>
  );
};

export default CatalogGroupRail;
